import React from 'react';
import clsx from 'clsx';
import { ACCENTS, AccentToken } from '../../theme/accents';

/**
 * Color tone for a status dot. `off` is the muted neutral used for disabled /
 * inactive states; the rest carry health meaning.
 *
 * Tones describe *meaning* (healthy/degraded/down), not card identity -- a
 * green "Enabled" dot looks the same on every entity type for consistency.
 */
export type StatusTone =
  | 'green'
  | 'emerald'
  | 'orange'
  | 'red'
  | 'amber'
  | 'off';

/**
 * A dot is a solid core plus a translucent ring of the same hue. The ring is a
 * `ring-*` utility rather than the old `shadow-lg shadow-<c>/30` glow, which at
 * large blur bled into neighbouring text and made every footer look lit up.
 */
const TONE_CLASSES: Record<StatusTone, string> = {
  green: 'bg-green-500 ring-2 ring-green-500/20',
  emerald: 'bg-emerald-500 ring-2 ring-emerald-500/20',
  orange: 'bg-orange-500 ring-2 ring-orange-500/20',
  red: 'bg-red-500 ring-2 ring-red-500/20',
  amber: 'bg-amber-500 ring-2 ring-amber-500/20',
  off: 'bg-semi-color-text-3',
};

interface StatusDotProps {
  tone: StatusTone;
  label: string;
  title?: string;
  className?: string;
}

/**
 * A status dot plus its label, used across every card footer for
 * enabled/disabled and health states. Render two side by side with a
 * StatusDivider between them to match the existing footer layout.
 */
const StatusDot: React.FC<StatusDotProps> = ({
  tone,
  label,
  title,
  className,
}) => {
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div className={clsx('w-2.5 h-2.5 rounded-full', TONE_CLASSES[tone])} />
      <span
        className="text-sm font-medium text-semi-color-text-1"
        title={title}
      >
        {label}
      </span>
    </div>
  );
};

/**
 * Thin vertical rule placed between two StatusDots in a footer. Neutral for
 * every accent -- it is chrome, not identity (see theme/accents.ts).
 */
export const StatusDivider: React.FC<{ accent?: AccentToken }> = ({
  accent = 'neutral',
}) => <div className={clsx('w-px h-4', ACCENTS[accent].divider)} />;

export default StatusDot;
